import { useEffect, useState } from 'react'

// How much notice the warning gives, when the page was served without the configured value —
// an old cached document, or a test rendering the app directly. The real number comes from
// EKO_SESSION_WARNING_MINUTES in app.env and is baked into index.html.
export const DEFAULT_WARNING_SECONDS = 5 * 60

// Written by the server on every response while a session is live (see web/session_policy.py).
// Unix seconds, deliberately not HttpOnly. Reading it is what lets the countdown work without a
// poll — and a poll would be worse than useless here, since the request would keep refreshing the
// very window it was asking about.
const DEADLINE_COOKIE = 'eko_session_expires'

function readDeadline(): number | null {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${DEADLINE_COOKIE}=(\\d+)`))
    if (match === null) {
        return null
    }
    return Number(match[1]) * 1000
}

/**
 * Seconds until the session expires, but only once that is close enough to be worth saying —
 * `null` the rest of the time, which is nearly always. `onExpired` fires if the deadline passes
 * with the page still open (a laptop that slept through it, say).
 *
 * The deadline is absolute, so the interval is only a prompt to recompute: a throttled background
 * tab or a suspended machine comes back with the right answer rather than a stale count. A missing
 * cookie means "don't know" — an old session from before this shipped, or cookies cleared — and is
 * deliberately silent. An expiry that slips through is still caught, by the 401 the next request
 * gets.
 */
export default function useSessionExpiry(
        active: boolean, warningSeconds: number, onExpired: () => void): number | null {
    const [secondsLeft, setSecondsLeft] = useState<number | null>(null)

    useEffect(() => {
        if (!active) {
            setSecondsLeft(null)
            return
        }
        const check = () => {
            const deadline = readDeadline()
            if (deadline === null) {
                setSecondsLeft(null)
                return
            }
            const left = Math.round((deadline - Date.now()) / 1000)
            if (left <= 0) {
                setSecondsLeft(null)
                onExpired()
            } else {
                setSecondsLeft(left <= warningSeconds ? left : null)
            }
        }
        check()
        const timer = window.setInterval(check, 1000)
        // Coming back to a tab that was throttled or asleep: recompute at once rather than
        // showing yesterday's count until the next tick.
        document.addEventListener('visibilitychange', check)
        return () => {
            window.clearInterval(timer)
            document.removeEventListener('visibilitychange', check)
        }
    }, [active, warningSeconds, onExpired])

    return secondsLeft
}
